import { resolveWorkspaceDeskFromHost } from '@/config/workspace-desk'

const STORAGE_KEY = 'amglobal.reception-desk.session-v1'

export type ReceptionDeskSessionEntry = {
  id: string
  displayId: string | null
  firstName: string
  lastName: string
  phoneNumber: string
  nationality: string
  designationLabel: string
  createdAt: string
  cvUploaded: boolean
}

export function isReceptionDeskHost(host: string = window.location.hostname): boolean {
  return resolveWorkspaceDeskFromHost(host) === 'reception'
}

export function normalizeReceptionPhone(value: string): string {
  return value.replace(/\D/g, '')
}

function readEntries(): ReceptionDeskSessionEntry[] {
  if (typeof sessionStorage === 'undefined') return []
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (row): row is ReceptionDeskSessionEntry =>
        row != null &&
        typeof row === 'object' &&
        typeof (row as ReceptionDeskSessionEntry).id === 'string' &&
        typeof (row as ReceptionDeskSessionEntry).firstName === 'string' &&
        typeof (row as ReceptionDeskSessionEntry).lastName === 'string' &&
        typeof (row as ReceptionDeskSessionEntry).phoneNumber === 'string',
    )
  } catch {
    return []
  }
}

function writeEntries(entries: ReceptionDeskSessionEntry[]): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
}

export function listReceptionDeskSessionEntries(): ReceptionDeskSessionEntry[] {
  return readEntries()
}

export function appendReceptionDeskSessionEntry(
  entry: Omit<ReceptionDeskSessionEntry, 'createdAt'> & { createdAt?: string },
): ReceptionDeskSessionEntry {
  const saved: ReceptionDeskSessionEntry = {
    ...entry,
    createdAt: entry.createdAt ?? new Date().toISOString(),
  }
  writeEntries([saved, ...readEntries()])
  return saved
}

export function markReceptionDeskSessionCvUploaded(candidateId: string): void {
  const next = readEntries().map((row) =>
    row.id === candidateId ? { ...row, cvUploaded: true } : row,
  )
  writeEntries(next)
}

export function findReceptionDeskSessionDuplicate(
  phoneNumber: string,
): ReceptionDeskSessionEntry | null {
  const needle = normalizeReceptionPhone(phoneNumber)
  if (!needle) return null
  return (
    readEntries().find((row) => normalizeReceptionPhone(row.phoneNumber) === needle) ?? null
  )
}

export function clearReceptionDeskSession(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(STORAGE_KEY)
}
